import { PARTNER_STATUS_LABELS, PARTNER_STATUS_COLORS, CLINIC_STATUS_LABELS, CLINIC_STATUS_COLORS } from './constants';

// Format partner status for display
export const formatPartnerStatus = (status) => ({
  label: PARTNER_STATUS_LABELS[status] || status,
  color: PARTNER_STATUS_COLORS[status] || 'default',
});

// Format clinic status for display
export const formatClinicStatus = (status) => ({
  label: CLINIC_STATUS_LABELS[status] || status,
  color: CLINIC_STATUS_COLORS[status] || 'default',
});

// Format phone number for display
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  // Add basic formatting (can be customized based on country)
  return phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3');
};

// Get initials from name (for avatar fallback)
export const getInitials = (name) => {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Format time (24h to 12h format - optional)
export const formatTime = (time, use24Hour = true) => {
  if (!time) return '';
  if (use24Hour) return time;

  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Build working hours display string
export const formatWorkingHoursDisplay = (day, hours) => {
  if (!hours || !hours.is_open) {
    return 'Closed';
  }
  return `${formatTime(hours.open_time)} - ${formatTime(hours.close_time)}`;
};

// Prepare form data for submission
export const preparePartnerFormData = (values) => {
  const data = {
    name: values.name?.trim(),
    email: values.email?.trim().toLowerCase(),
    phone: values.phone?.trim(),
    description: values.description?.trim() || null,
    status: values.status,
  };

  // Add MAIB credentials if provided
  if (values.maib_merchant_id) {
    data.maib_merchant_id = values.maib_merchant_id.trim();
  }
  if (values.maib_merchant_name) {
    data.maib_merchant_name = values.maib_merchant_name.trim();
  }
  if (values.maib_terminal_id) {
    data.maib_terminal_id = values.maib_terminal_id.trim();
  }
  if (values.maib_signature_key) {
    data.maib_signature_key = values.maib_signature_key.trim();
  }

  return data;
};

// Prepare clinic form data for submission
export const prepareClinicFormData = (values, partnerId) => {
  const data = {
    b2b_partner_id: partnerId,
    name: values.name?.trim(),
    address: values.address?.trim(),
    city: values.city?.trim(),
    phone: values.phone?.trim(),
    is_active: values.status === 'active',
  };

  // Optional fields
  if (values.state) {
    data.state = values.state.trim();
  }
  if (values.email) {
    data.email = values.email.trim();
  }
  if (values.postal_code) {
    data.postal_code = values.postal_code.trim();
  }
  if (values.description) {
    data.description = values.description.trim();
  }
  if (values.latitude) {
    data.latitude = values.latitude;
  }
  if (values.longitude) {
    data.longitude = values.longitude;
  }

  return data;
};

// Generate table pagination config
export const getTablePaginationConfig = (pagination, onChange) => ({
  current: pagination.current_page || 1,
  pageSize: pagination.per_page || 10,
  total: pagination.total || 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
  onChange: (page, pageSize) => onChange({ page, per_page: pageSize }),
  onShowSizeChange: (current, size) => onChange({ page: 1, per_page: size }),
});

// Check if object has changes
export const hasFormChanges = (original, current) => {
  return JSON.stringify(original) !== JSON.stringify(current);
};

// Safe JSON parse
export const safeJsonParse = (json, fallback = null) => {
  try {
    return JSON.parse(json);
  } catch {
    return fallback;
  }
};

// Debounce function for search inputs
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Generate filter query params
export const buildFilterParams = (filters) => {
  const params = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params[key] = value;
    }
  });

  return params;
};

// Sort working hours by day order
export const sortWorkingHoursByDay = (workingHours) => {
  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const sorted = {};

  dayOrder.forEach((day) => {
    if (workingHours[day]) {
      sorted[day] = workingHours[day];
    }
  });

  return sorted;
};